import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI } from '../utils/axios';

const useAuthStore = create(
  persist(
    (set, get) => ({
      // 상태
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // 로그인 액션
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        
        try {
          console.log('[AUTH]', 'Login attempt for:', email);
          
          const result = await authAPI.login(email, password);
          
          if (result.success) {
            const { token, user } = result.data;
            
            set({
              user: {
                id: user.user_id,
                user_id: user.user_id,
                name: user.username,
                email: user.email,
                organization: '한국병원약사회', // 추후 백엔드에서 제공될 수 있음
                department: '약제부', // 추후 백엔드에서 제공될 수 있음
                registration_code: user.registration_code || null, // 🆕 인증코드 저장
                avatar: null
              },
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null
            });

            console.log('[AUTH]', 'Login successful:', user.username);
            return { success: true, message: result.message };

          } else {
            set({
              isLoading: false,
              error: result.message
            });
            console.error('[AUTH]', 'Login failed:', result.message);
            return { success: false, error: result.message };
          }

        } catch (error) {
          const errorMessage = '로그인 중 오류가 발생했습니다.';
          set({
            isLoading: false,
            error: errorMessage
          });
          console.error('[AUTH]', 'Login error:', error);
          return { success: false, error: errorMessage };
        }
      },

      // 회원가입 액션
      register: async (userData) => {
        set({ isLoading: true, error: null });
        
        try {
          console.log('[AUTH]', 'Register attempt for:', userData.email);
          
          // 🆕 인증코드 포함하여 전달

          console.log('Registration Data Sent:', {
            username: userData.username,
            email: userData.email,
            password: userData.password,
            registration_code: userData.registration_code
          });

          const result = await authAPI.register({
            username: userData.username,
            email: userData.email,
            password: userData.password,
            registration_code: userData.registration_code  // 🆕 인증코드 추가
          });
          
          if (result.success) {
            // 회원가입 성공 후 자동 로그인
            const loginResult = await get().login(userData.email, userData.password);
            
            if (loginResult.success) {
              console.log('[AUTH]', 'Registration and auto-login successful');
              return { success: true, message: '회원가입이 완료되었습니다.' };
            } else {
              // 회원가입은 성공했지만 자동 로그인 실패
              set({ isLoading: false, error: null });
              return { 
                success: true, 
                message: '회원가입이 완료되었습니다. 로그인해 주세요.',
                autoLoginFailed: true
              };
            }

          } else {
            set({
              isLoading: false,
              error: result.message
            });
            console.error('[AUTH]', 'Registration failed:', result.message);
            return { success: false, error: result.message };
          }

        } catch (error) {
          const errorMessage = '회원가입 중 오류가 발생했습니다.';
          set({
            isLoading: false,
            error: errorMessage
          });
          console.error('[AUTH]', 'Registration error:', error);
          return { success: false, error: errorMessage };
        }
      },

      // 로그아웃 액션
      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        });
        console.log('[AUTH]', 'Logout successful');
      },

      // 에러 클리어
      clearError: () => {
        set({ error: null });
      },

      // 사용자 정보 업데이트
      updateUser: (userData) => {
        const currentUser = get().user;
        if (currentUser) {
          const updatedUser = { ...currentUser, ...userData };
          set({ user: updatedUser });
          console.log('[AUTH]', 'User updated:', updatedUser);
        }
      },

      // 토큰 유효성 검사 (필요시 구현)
      validateToken: async () => {
        const { token, isAuthenticated } = get();
        
        if (!token || !isAuthenticated) {
          return false;
        }

        // 토큰 만료 확인 로직 (JWT 디코딩)
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const currentTime = Math.floor(Date.now() / 1000);
          
          if (payload.exp && payload.exp < currentTime) {
            console.warn('[AUTH]', 'Token expired');
            get().logout();
            return false;
          }
          
          return true;
        } catch (error) {
          console.error('[AUTH]', 'Token validation error:', error);
          get().logout();
          return false;
        }
      },

      // 인증 상태 체크
      checkAuth: () => {
        const { user, token, isAuthenticated } = get();
        return user && token && isAuthenticated;
      },

      // 토큰 만료시 자동 로그아웃 (필요시 호출)
      handleTokenExpiry: () => {
        console.warn('[AUTH]', 'Token expired, logging out');
        get().logout();
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
);

export default useAuthStore;